import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {ValidatorFn, Validators}             from '@angular/forms';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {map}                                            from 'rxjs/operators';
import {ObjectFactory}                                  from 'ts-tooling';
import {ApDynformsComponent}                            from '../../ap-dynforms/ap-dynforms.component';
import {
  ApDynformsConfigFieldset
}                                                       from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigTextbox,
  ApDynformsConfigTextboxType
}                                                       from '../../ap-dynforms/config/ap-dynforms-config-textbox';
import {ApDynformsValidator}                            from '../../ap-dynforms/ap-dynforms-validator';
import {
  ApDynformsConfigCheckbox
}                                                       from '../../ap-dynforms/config/ap-dynforms-config-checkbox';
import {
  ApDynformsConfigComboBox
}                                                       from '../../ap-dynforms/config/ap-dynforms-config-combobox';
import {DriverStore}                                    from '../../stores/docu/driver.store';
import {FarmUsersStore}                                 from '../../stores/farm/farm.users.store';
import {FormStore, IEntryComponent}                     from '../../stores/layout/form.store';
import {nameOf}                                         from '../../ap-core/utils/ap-name-of';
import {IUsersEditFormData}                             from './types/ap-users-edit.types';
import IDriver = Data.DocuContext.Driver.IDriver;
import IFarmUsers = Data.Authentication.IFarmUsers;
import IUser = Data.Authentication.IUser;

@Component({
  selector: 'ap-users-edit',
  template: `
    <ap-dynforms [fieldsets]="formConfig" [loading$]="loading$" [caption]="'Permission_Feature_Edit_Users'">
      <div class="ap-form-actions" dynforms.action>
        <button id="button_user_edit_cancel"
                type="button"
                kendoButton class="ap-form-button-left"
                (apSingleClick)="onCancelClick()">{{ 'Global__Cancel' | translate }}
        </button>
        <button id="button_user_edit_save"
                type="button" [disabled]="!dynForm.form.valid"
                kendoButton [themeColor]="'primary'" class="ap-form-button-right"
                (apSingleClick)="onSubmitClick()">{{ 'Global__Save' | translate }}
        </button>
      </div>
    </ap-dynforms>
  `,
  standalone: false
})
export class ApUsersEditComponent implements OnInit, IEntryComponent {
  /**
   * Implements IEntryComponent
   */
  public Caption$: BehaviorSubject<string> = new BehaviorSubject('Permission_Feature_Edit_Users');
  @ViewChild(ApDynformsComponent, {static: true})
  public dynForm: ApDynformsComponent;

  @Input() public data: IFarmUsers;
  public loading$: Observable<boolean> = of(true);
  public formConfig: ApDynformsConfigFieldset[];

  private _formData: IUsersEditFormData | undefined;
  private _isEditMode = false;

  constructor(private formStore: FormStore,
              private driverStore: DriverStore,
              private farmUsersStore: FarmUsersStore) {
  }

  public ngOnInit(): void {
    this._isEditMode = !!this.data;
    this.setLoading();
    this.setFormData();
    this.setFormFields();
  }

  public onCancelClick(): void {
    this.formStore.closeForm();
  }

  public onSubmitClick(): void {
    const formData = this.dynForm.form.value as IUsersEditFormData;
    const driver = this.driverStore.getDriverById(formData.WorkerId);
    const copiedFarmUser = this.getFarmUserObject();
    copiedFarmUser.User.Forename = formData.FirstName;
    copiedFarmUser.User.Surname = formData.LastName;
    copiedFarmUser.IsActive = formData.IsActive;
    copiedFarmUser.Driver = driver;
    copiedFarmUser.IsDriver = !!driver;
    this.farmUsersStore.update(copiedFarmUser);
    this.formStore.closeForm();
  }

  private setLoading(): void {
    this.loading$ = combineLatest([
      this.driverStore.Loading$,
      this.farmUsersStore.Loading$
    ]).pipe(
      map(loadings => loadings.some(x => x))
    );
  }

  private setFormData(): void {
    this._formData = {
      FirstName: this.data?.User?.Forename,
      LastName: this.data?.User?.Surname,
      Email: this.data?.User?.EMail,
      IsActive: this.data?.IsActive ?? false,
      WorkerId: this.data?.Driver?.Id
    };
  }

  private setFormFields(): void {
    this.formConfig = [
      new ApDynformsConfigFieldset({
        key: 'Global__General_Information',
        legend: 'Global__General_Information',
        config: [
          new ApDynformsConfigTextbox({
            key: nameOf<IUsersEditFormData>('FirstName'),
            label: 'Global__Forename',
            type: ApDynformsConfigTextboxType.Text,
            value: this._formData.FirstName,
            validators: this.getValidators()
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<IUsersEditFormData>('LastName'),
            label: 'Global__Surname',
            type: ApDynformsConfigTextboxType.Text,
            value: this._formData.LastName,
            validators: this.getValidators()
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<IUsersEditFormData>('Email'),
            label: 'Global__Email',
            type: ApDynformsConfigTextboxType.Email,
            value: this._formData.Email,
            disabled: this._isEditMode,
            validators: [
              ...this.getValidators(),
              new ApDynformsValidator({
                validator: Validators.email,
                errorKey: 'Settings__Msg_Vali_Value_Required',
              })
            ]
          }),
          new ApDynformsConfigCheckbox({
            key: nameOf<IUsersEditFormData>('IsActive'),
            label: 'Global__Active',
            value: this._formData.IsActive
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IUsersEditFormData>('WorkerId'),
            label: 'Global__Worker',
            valueField: nameOf<IDriver>('Id'),
            textField: nameOf<IDriver>('FullName'),
            value: this._formData.WorkerId,
            options: combineLatest([
              this.driverStore.Drivers$,
              this.farmUsersStore.FarmUsers$
            ]).pipe(
              map(([drivers, farmUsers]) => {
                return drivers.filter(d => d.Id === this._formData.WorkerId || !farmUsers.some(fu => fu.Driver?.Id === d.Id));
              })
            ),
            valuePrimitive: true
          })
        ]
      })
    ];
  }

  private getValidators(): ApDynformsValidator<ValidatorFn>[] {
    return [
      new ApDynformsValidator({
        validator: Validators.required,
        errorKey: 'Settings__Msg_Vali_Value_Required',
      }),
      new ApDynformsValidator({
        validator: Validators.nullValidator,
        errorKey: 'Settings__Msg_Vali_Value_Required'
      }),
      new ApDynformsValidator({
        validator: Validators.minLength(1),
        errorKey: 'Settings__Msg_Vali_Value_Required'
      })
    ];
  }

  private getFarmUserObject(): IFarmUsers {
    if (this._isEditMode) {
      return ObjectFactory.Copy(this.data) as IFarmUsers;
    }
    return {
      User: {} as IUser,
      IsDriver: false,
      IsActive: true
    } as IFarmUsers;
  }
}
